/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileMagnifyingGlass, ArrowClockwise, CheckCircle, WarningCircle, Eye } from "@phosphor-icons/react";

/**
 * HMLR "Search" card on the case detail sidebar. Currently ships the
 * OC1 (Official Copy of Register) request flow — the single most
 * common HMLR call in conveyancing. Later revisions will layer on
 * OS1 (search of the register), K16 (bankruptcy), K15 (full search)
 * behind the same UI shell.
 *
 * Backed by:
 *   POST /api/cases/{id}/hmlr-bg/oc1
 *   GET  /api/cases/{id}/hmlr-bg/requests
 */
export default function HmlrRequestsCard({ caseId, isSolicitor }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [titleNumber, setTitleNumber] = useState("");
  const [viewResponse, setViewResponse] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/hmlr-bg/requests`);
      setItems(data.items || []);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load HMLR requests");
      }
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const submitOc1 = async () => {
    const tn = titleNumber.trim().toUpperCase();
    if (!tn) {
      toast.error("Enter a title number first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/hmlr-bg/oc1`, {
        title_number: tn,
        include_title_plan: true,
      });
      toast.success(`OC1 submitted — message ID ${data?.record?.message_id?.slice(0, 20)}…`);
      setTitleNumber("");
      fetchItems();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(`OC1 submission failed${detail ? `: ${formatApiErrorDetail(detail)}` : ""}`, { duration: 10000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="hmlr-requests-card">
      <div className="flex items-center gap-2 mb-3">
        <FileMagnifyingGlass weight="bold" className="w-4 h-4 text-slate-500" />
        <h4 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          HMLR searches
        </h4>
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Direct Business Gateway integration — request an Official Copy (OC1) of the register straight from HM Land Registry. Response arrives in seconds during business hours, or later via the Outstanding Requests widget on the dashboard.
      </p>

      {isSolicitor && (
        <div className="flex gap-2 mb-4" data-testid="hmlr-oc1-form">
          <Input
            placeholder="e.g. NGL123456"
            value={titleNumber}
            onChange={(e) => setTitleNumber(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") submitOc1(); }}
            disabled={submitting}
            className="text-xs h-8 font-mono uppercase"
            data-testid="hmlr-oc1-title-input"
          />
          <Button
            onClick={submitOc1}
            disabled={submitting || !titleNumber.trim()}
            size="sm"
            className="h-8 text-xs shrink-0"
            data-testid="hmlr-oc1-submit-btn"
          >
            {submitting ? (<><ArrowClockwise weight="bold" className="w-3 h-3 mr-1 animate-spin" /> Submitting…</>) : "Request OC1"}
          </Button>
        </div>
      )}

      <div>
        <p className="text-[11px] font-medium text-slate-600 mb-2">Recent requests</p>
        {loading ? (
          <p className="text-xs text-slate-400 py-2 text-center">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-slate-400 py-2 text-center" data-testid="hmlr-requests-empty">
            No HMLR requests on this case yet.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-100 rounded" data-testid="hmlr-requests-list">
            {items.map((r, idx) => (
              <li
                key={r.id || idx}
                className="flex items-center gap-2 px-2 py-2 hover:bg-slate-50"
                data-testid={`hmlr-request-row-${r.message_id || idx}`}
              >
                {r.ok ? (
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                ) : (
                  <WarningCircle weight="fill" className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">
                    <span className="uppercase text-[10px] text-slate-400 mr-1.5">{r.service_key}</span>
                    {r.title_number || "-"}
                    {r.pdf_document_id && (
                      <span className="ml-1.5 inline-flex items-center px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[9px] font-semibold uppercase" title="Saved to case Documents">
                        PDF
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {r.requested_at ? new Date(r.requested_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                    {r.requested_by_name && ` · ${r.requested_by_name}`}
                    {r.error && ` · ${r.error.slice(0, 60)}`}
                  </p>
                </div>
                {r.response && (
                  <button
                    type="button"
                    onClick={() => setViewResponse(r)}
                    className="text-slate-400 hover:text-slate-700 shrink-0"
                    data-testid={`hmlr-request-view-${r.message_id || idx}`}
                    title="View response"
                  >
                    <Eye weight="bold" className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Response viewer */}
      <Dialog open={!!viewResponse} onOpenChange={(o) => !o && setViewResponse(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="hmlr-response-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {(viewResponse?.service_key || "").toUpperCase()} response — {viewResponse?.title_number}
            </DialogTitle>
          </DialogHeader>
          <div className="text-[11px] text-slate-500 font-mono mb-2">
            Message ID: {viewResponse?.message_id}
          </div>
          <pre className="flex-1 overflow-auto text-[11px] bg-slate-900 text-emerald-300 p-4 rounded font-mono whitespace-pre-wrap break-all">
            {viewResponse?.response ? JSON.stringify(viewResponse.response, null, 2) : "(empty response)"}
          </pre>
          <div className="pt-3 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(viewResponse?.response || {}, null, 2));
                toast.success("Copied JSON to clipboard");
              }}
              data-testid="hmlr-response-copy-btn"
            >
              Copy JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={savingPdf}
              onClick={async () => {
                if (!viewResponse?.id) return;
                setSavingPdf(true);
                try {
                  const { data } = await API.post(
                    `/cases/${caseId}/hmlr-bg/requests/${viewResponse.id}/save-as-pdf`,
                  );
                  toast.success(`Saved "${data?.document?.original_filename}" to case Documents`, { duration: 6000 });
                  fetchItems();
                } catch (err) {
                  const detail = err?.response?.data?.detail;
                  toast.error(`Save-as-PDF failed${detail ? `: ${formatApiErrorDetail(detail)}` : ""}`, { duration: 8000 });
                } finally {
                  setSavingPdf(false);
                }
              }}
              data-testid="hmlr-response-save-pdf-btn"
            >
              {savingPdf ? "Saving…" : "Save as PDF to Docs"}
            </Button>
            <Button size="sm" onClick={() => setViewResponse(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
